import { css } from "@emotion/css";
import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { ListItemContent, List, ListItemLink, Pagination, SearchInput, ListTitle, ListDescription } from "@octopusdeploy/design-system-components";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { LicenseStatusResource, UserRoleResource, Repository, ResourceCollection } from "@octopusdeploy/octopus-server-client";
import { PermissionsMode, Permission } from "@octopusdeploy/octopus-server-client";
import type { QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import { links } from "@octopusdeploy/portal-routes";
import { useCallback } from "react";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { restrictedPermissionsCalloutConfig } from "~/components/RestrictedPermissionsCallout";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
interface UserRolesPageProps {
    loaderData: UserRolesPageLoaderData;
    queryParams: UserRolesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<UserRolesPageQueryParams>;
}
interface UserRolesPageLoaderData {
    roles: ResourceCollection<UserRoleResource>;
    licenseStatus: LicenseStatusResource;
}
interface UserRolesPageQueryParams {
    roleName: string;
    page: number | undefined;
    pageSize: number | undefined;
}
const defaultPageSize = 30;
const pageSizeOptions = [defaultPageSize, 50, 100];
export const userRolesPageTitle = "User Roles";
export async function userRolesPageLoader(repository: Repository, queryParams: UserRolesPageQueryParams): Promise<UserRolesPageLoaderData> {
    const rolesAsync = getRoles(repository, queryParams.roleName, queryParams.page, queryParams.pageSize);
    const licenseStatusAsync = repository.Licenses.getCurrentStatus();
    return {
        roles: await rolesAsync,
        licenseStatus: await licenseStatusAsync,
    };
}
export function UserRolesPage({ loaderData, queryParams, setQueryParams }: UserRolesPageProps) {
    const [debouncedRoleName, setDebouncedRoleName] = useDebouncedExternalState(queryParams.roleName, (value) => setQueryParams({ ...queryParams, roleName: value, page: 1 }), 200);
    const { result: roles } = useQuery((repository) => getRoles(repository, queryParams.roleName, queryParams.page, queryParams.pageSize), [queryParams.roleName, queryParams.page, queryParams.pageSize], "Load user roles", {
        initialResult: loaderData.roles,
    });
    const addCustomRolePageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.UserRoleEdit }),
        label: "Add custom role",
        path: links.createUserRolePage.generateUrl(),
    };
    const onPageChange = useCallback((page: number) => {
        setQueryParams({ ...queryParams, page });
    }, [queryParams, setQueryParams]);
    const onPageSizeChange = useCallback((pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize, page: 1 });
    }, [queryParams, setQueryParams]);
    return (<PageContent header={{ title: userRolesPageTitle, primaryAction: addCustomRolePageAction }} callout={loaderData.licenseStatus.PermissionsMode === PermissionsMode.Restricted ? restrictedPermissionsCalloutConfig : undefined} filters={{ inputs: [<SearchInput value={debouncedRoleName} onChange={setDebouncedRoleName} accessibleName="Filter user roles by name" placeholder="Filter by name"/>] }} pagination={{
            ui: (<Pagination label="User Roles" totalResults={roles.TotalResults} itemsPerPageOptions={pageSizeOptions} selectedItemsPerPage={roles.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "topAndBottom",
        }}>
            <List items={roles.Items} renderRow={(props) => <UserRoleRow role={props.item}/>}/>
        </PageContent>);
}
function UserRoleRow({ role }: {
    role: UserRoleResource;
}) {
    return (<ListItemLink href={links.userRolePage.generateUrl({ roleId: role.Id })}>
            <ListItemContent>
                <ListTitle>{role.Name}</ListTitle>
                {role.Description && <ListDescription description={role.Description}/>}
            </ListItemContent>
        </ListItemLink>);
}
const userRoleDescriptionStyles = css({
    color: themeTokens.color.text.primary,
    margin: 0,
});
async function getRoles(repository: Repository, roleName = "", page = 1, pageSize = defaultPageSize): Promise<ResourceCollection<UserRoleResource>> {
    return repository.UserRoles.list({
        partialName: roleName,
        skip: (page - 1) * pageSize,
        take: pageSize,
    });
}
